import { useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getSurvey,
  patchSurvey,
  publishDefinition,
  openDiffusion,
  closeDiffusion,
  getSurveyDefinitionForEditing,
  getDpiaStatus,
  getErrorMessage,
} from '@/lib/api';
import type {
  Block,
  Choice,
  Item,
  ItemType,
  Section,
  SurveyDefinition,
} from '@webforge-survey/shared';
import { Icon, IconButton, Loading, Badge, QrCodeDialog } from '@/components/ui';
import DpiaDialog from './DpiaDialog';
import {
  BranchContextProvider,
  BranchEditor,
  ConditionPicker,
  ExpertRelevantInput,
  WardenLintBadge,
  useBranchContext,
} from './BranchingControls';

/** Vrai si l'erreur de diffusion est le gating AIPD (RGPD art. 35). */
function isAipdError(err: unknown): boolean {
  const data = (err as { response?: { data?: { code?: string; error?: { code?: string } } } }).response?.data;
  const code = data?.code ?? data?.error?.code ?? '';
  return code === 'AIPD_REQUIRED' || /aipd|dpia/i.test(getErrorMessage(err, ''));
}

/**
 * Message d'erreur lisible pour les actions de (re)diffusion / publication.
 * Cas explicite de l'AIPD-gating (RGPD) : on guide l'utilisateur au lieu de
 * laisser une erreur opaque (cause du « bug » de republication signalé).
 */
function diffusionErrorMessage(err: unknown, fallback: string): string {
  const data = (err as { response?: { data?: { code?: string; error?: { code?: string } } } }).response?.data;
  const code = data?.code ?? data?.error?.code ?? '';
  const msg = getErrorMessage(err, fallback);
  if (code === 'AIPD_REQUIRED' || /aipd|dpia/i.test(msg)) {
    return "Données sensibles (art. 9) : une AIPD/DPIA signée est requise avant de (re)diffuser cette enquête (RGPD). Rapprochez-vous de votre DPO.";
  }
  return msg;
}

/**
 * Éditeur d'enquête (structuré, sans drag-and-drop).
 *
 * Route : /workspaces/:id/surveys/:surveyId/edit
 *
 * Garde-fous RGPD :
 *  - identityMode verrouillé après 1ʳᵉ diffusion (RGPD_PLAN §1).
 *  - meta.sensitive / meta.pii_risk exposés par item (data-minimization).
 *  - validation de l'unicité des `name` avant publication.
 */
export default function SurveyEditorPage(): JSX.Element {
  const { id: workspaceId, surveyId } = useParams<{ id: string; surveyId: string }>();
  const queryClient = useQueryClient();

  const surveyQuery = useQuery({
    queryKey: ['survey', workspaceId, surveyId],
    queryFn: () => getSurvey(workspaceId ?? '', surveyId ?? ''),
    enabled: Boolean(workspaceId && surveyId),
  });

  // Charge la définition COURANTE via l'endpoint AUTHENTIFIÉ (tout statut).
  // NB : on n'utilise PAS l'endpoint public `/s/:slug` ici — il est réservé aux
  // enquêtes publiées, donc une enquête « fermée » y renverrait 404 et l'éditeur
  // afficherait « Blocs (0) » à tort (régression corrigée).
  const defQuery = useQuery({
    queryKey: ['def-edit', workspaceId, surveyId, surveyQuery.data?.currentVersionId],
    queryFn: async () => {
      if (!workspaceId || !surveyId) return null;
      if (!surveyQuery.data) return null;
      if (!surveyQuery.data.currentVersionId) return null;
      try {
        return await getSurveyDefinitionForEditing(workspaceId, surveyId);
      } catch {
        return null;
      }
    },
    enabled: Boolean(surveyQuery.data && workspaceId && surveyId),
  });

  // État de l'AIPD (owner only) — pour le bouton « Signer l'AIPD » et lever le
  // gating de diffusion sensible. En 403 (non-owner) la query reste sans data.
  const dpiaQuery = useQuery({
    queryKey: ['dpia', workspaceId, surveyId],
    queryFn: () => getDpiaStatus(workspaceId ?? '', surveyId ?? ''),
    enabled: Boolean(workspaceId && surveyId),
    retry: false,
  });

  const [def, setDef] = useState<SurveyDefinition | null>(null);
  const [titleDraft, setTitleDraft] = useState('');
  const [warn, setWarn] = useState<string[]>([]);
  const [qrOpen, setQrOpen] = useState(false);
  const [dpiaOpen, setDpiaOpen] = useState(false);
  // Quand l'AIPD est signée à la suite d'un échec de diffusion, on relance
  // automatiquement l'ouverture de diffusion.
  const [diffuseAfterSign, setDiffuseAfterSign] = useState(false);

  // Initialiser la def éditable au premier load.
  useEffect(() => {
    if (def !== null) return;
    if (!surveyQuery.data) return;
    if (defQuery.isPending) return;
    if (defQuery.data) {
      setDef(defQuery.data);
    } else {
      // Pas de définition encore publiée — commencer vide.
      // identitySchema injecté depuis le survey (requis par la validation de
      // publication quand identityMode ≠ anonymous).
      const schema = surveyQuery.data.identitySchema as SurveyDefinition['identitySchema'] | null | undefined;
      setDef({
        id: surveyQuery.data.slug,
        definitionVersion: 1,
        locale: 'fr',
        title: surveyQuery.data.title,
        identityMode: surveyQuery.data.identityMode,
        ...(schema ? { identitySchema: schema } : {}),
        blocks: [],
      });
    }
    setTitleDraft(surveyQuery.data.title);
  }, [def, surveyQuery.data, defQuery.data, defQuery.isPending]);

  const isLocked = useMemo(() => {
    return Boolean(surveyQuery.data?.currentVersionId);
  }, [surveyQuery.data]);

  const titleMutation = useMutation({
    mutationFn: (newTitle: string) =>
      patchSurvey(workspaceId ?? '', surveyId ?? '', { title: newTitle }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['survey', workspaceId, surveyId] });
      void queryClient.invalidateQueries({ queryKey: ['surveys', workspaceId] });
    },
  });

  const publishMutation = useMutation({
    mutationFn: async (d: SurveyDefinition) => {
      if (!workspaceId || !surveyId) throw new Error('Workspace ou survey introuvable.');
      return publishDefinition(workspaceId, surveyId, d);
    },
    onSuccess: () => {
      setWarn([]);
      void queryClient.invalidateQueries({ queryKey: ['survey', workspaceId, surveyId] });
      void queryClient.invalidateQueries({ queryKey: ['def'] });
      void queryClient.invalidateQueries({ queryKey: ['surveys', workspaceId] });
    },
    onError: (err) => setWarn([diffusionErrorMessage(err, 'Publication impossible.')]),
  });

  const diffusionMutation = useMutation({
    mutationFn: async (action: 'open' | 'close') => {
      if (!workspaceId || !surveyId) throw new Error('Workspace ou survey introuvable.');
      return action === 'open'
        ? openDiffusion(workspaceId, surveyId)
        : closeDiffusion(workspaceId, surveyId);
    },
    onSuccess: () => {
      setWarn([]);
      void queryClient.invalidateQueries({ queryKey: ['survey', workspaceId, surveyId] });
      void queryClient.invalidateQueries({ queryKey: ['surveys', workspaceId] });
    },
    onError: (err) => {
      setWarn([diffusionErrorMessage(err, 'Action de diffusion impossible.')]);
      // Gating AIPD : on ouvre directement l'écran de signature (au lieu de
      // laisser l'utilisateur devant un mur sans solution).
      if (isAipdError(err)) {
        setDiffuseAfterSign(true);
        setDpiaOpen(true);
      }
    },
  });

  if (!workspaceId || !surveyId) return <p>Lien invalide.</p>;
  if (surveyQuery.isPending || !def) return <Loading label="Chargement de l'éditeur…" />;
  if (surveyQuery.isError) {
    return (
      <p role="alert" className="field-error">
        <span aria-hidden="true">⚠</span>
        <span>{getErrorMessage(surveyQuery.error)}</span>
      </p>
    );
  }

  const survey = surveyQuery.data!;

  const updateDef = (next: SurveyDefinition): void => setDef(next);

  const updateBlock = (idx: number, patch: Partial<Block>): void => {
    const blocks = [...def.blocks];
    blocks[idx] = { ...blocks[idx]!, ...patch };
    updateDef({ ...def, blocks });
  };
  const moveBlock = (idx: number, delta: -1 | 1): void => {
    const blocks = [...def.blocks];
    const target = idx + delta;
    if (target < 0 || target >= blocks.length) return;
    [blocks[idx], blocks[target]] = [blocks[target]!, blocks[idx]!];
    updateDef({ ...def, blocks });
  };
  const removeBlock = (idx: number): void => {
    if (!confirm('Supprimer ce bloc et tous ses items ?')) return;
    const blocks = def.blocks.filter((_, i) => i !== idx);
    updateDef({ ...def, blocks });
  };
  const addBlock = (): void => {
    const id = `b${def.blocks.length + 1}`;
    updateDef({
      ...def,
      blocks: [
        ...def.blocks,
        { id, title: `Bloc ${def.blocks.length + 1}`, items: [] },
      ],
    });
  };

  const handlePublish = (): void => {
    setWarn([]);
    // Validations rapides : unicité des `name` (items directs ET de section).
    const names = new Set<string>();
    const duplicates: string[] = [];
    const empty: string[] = [];
    const checkItems = (items: Item[] | undefined, where: string): void => {
      for (const it of items ?? []) {
        if (it.type === 'note') continue;
        if (!it.name) empty.push(`(${where})`);
        else if (names.has(it.name)) duplicates.push(it.name);
        else names.add(it.name);
      }
    };
    for (const b of def.blocks) {
      checkItems(b.items, `bloc ${b.id}`);
      for (const s of b.sections ?? []) checkItems(s.items, `bloc ${b.id} / section ${s.id}`);
    }
    if (duplicates.length || empty.length) {
      setWarn([
        ...duplicates.map((n) => `name dupliqué : ${n}`),
        ...empty.map((b) => `name manquant ${b}`),
      ]);
      return;
    }
    if (def.blocks.length === 0) {
      setWarn(['Aucun bloc — ajouter au moins un bloc avant publication.']);
      return;
    }
    // Garantir l'identitySchema dans la def publiée pour optional/required
    // (la validation backend le requiert).
    const surveySchema = survey.identitySchema as SurveyDefinition['identitySchema'] | null | undefined;
    const toPublish: SurveyDefinition =
      def.identityMode !== 'anonymous' && !def.identitySchema && surveySchema
        ? { ...def, identitySchema: surveySchema }
        : def;
    if (toPublish.identityMode !== 'anonymous' && !toPublish.identitySchema) {
      setWarn(['Schéma d\'identité manquant — configurez les champs d\'identité avant publication.']);
      return;
    }
    publishMutation.mutate(toPublish);
  };

  // URL publique → wizard (parcours par défaut). `/s/:slug` redirige aussi vers
  // /wizard via le routeur, mais on émet directement la bonne URL pour les
  // partages (e-mail, copier dans le presse-papier, etc.).
  const publicUrl =
    survey.status === 'published' ? `${window.location.origin}/s/${survey.slug}/wizard` : null;

  return (
    <div className="space-y-5">
      {/* Sticky editor header */}
      <header className="sticky top-14 z-20 -mx-4 sm:-mx-6 px-4 sm:px-6 py-3 bg-bg/95 backdrop-blur-sm border-b border-border">
        <div className="flex items-start gap-4 flex-wrap">
          <Link
            to={`/workspaces/${workspaceId}/surveys`}
            className="text-sm text-text-muted hover:text-text inline-flex items-center gap-1.5 mt-1"
          >
            <Icon.ChevronRight className="rotate-180" size={14} /> Enquêtes
          </Link>
          <div className="min-w-0 flex-1">
            <input
              type="text"
              className="text-xl font-semibold w-full bg-transparent border-0 px-0 focus-visible:outline-none focus-visible:ring-0 focus:border-b focus:border-primary tracking-tight"
              style={{ minHeight: 32 }}
              value={titleDraft}
              onChange={(e) => setTitleDraft(e.target.value)}
              onBlur={() => {
                if (titleDraft.trim() && titleDraft !== survey.title) {
                  titleMutation.mutate(titleDraft.trim());
                }
              }}
              aria-label="Titre de l'enquête"
            />
            <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-text-muted">
              <Badge tone={statusTone(survey.status)}>{statusLabel(survey.status)}</Badge>
              <Badge tone={isLocked ? 'lock' : 'info'}>
                {identityLabel(def.identityMode)}
                {isLocked && <Icon.Lockfilled className="ml-1" />}
              </Badge>
              <span>·</span>
              <span className="font-mono">/{survey.slug}</span>
              <span>·</span>
              <span>v{survey.definitionVersion === 0 ? '—' : survey.definitionVersion}</span>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {dpiaQuery.data && (
              <button
                type="button"
                className="btn-secondary"
                onClick={() => {
                  setDiffuseAfterSign(false);
                  setDpiaOpen(true);
                }}
                title={
                  dpiaQuery.data.signed
                    ? `AIPD signée v${dpiaQuery.data.version} le ${dpiaQuery.data.signedAtDay}`
                    : "Signer l'AIPD (requise pour diffuser une enquête sensible)"
                }
              >
                <Icon.Shield /> {dpiaQuery.data.signed ? 'AIPD signée' : "Signer l'AIPD"}
              </button>
            )}
            {survey.status !== 'published' && survey.currentVersionId && (
              <button
                type="button"
                className="btn-secondary"
                disabled={diffusionMutation.isPending}
                onClick={() => diffusionMutation.mutate('open')}
              >
                <Icon.Globe /> Ouvrir la diffusion
              </button>
            )}
            {survey.status === 'published' && (
              <button
                type="button"
                className="btn-secondary"
                disabled={diffusionMutation.isPending}
                onClick={() => diffusionMutation.mutate('close')}
              >
                Fermer la diffusion
              </button>
            )}
            <button
              type="button"
              className="btn-primary"
              disabled={publishMutation.isPending}
              onClick={handlePublish}
            >
              {publishMutation.isPending ? 'Publication…' : 'Publier nouvelle version'}
            </button>
          </div>
        </div>
      </header>

      {def.identityMode !== 'anonymous' && (
        <p className="notice flex items-start gap-2">
          <Icon.Lock />
          <span>
            Mode <strong>{def.identityMode === 'required' ? 'identifié (obligatoire)' : 'opt-in identité'}</strong> :
            les réponses pourront être consultées <strong>reliées à l'identité</strong> du répondant
            (vue par répondant, accès tracé). Le <strong>consentement / la notice de l'enquête doivent
            annoncer ce traitement identifié</strong> et sa finalité (RGPD art. 13).
          </span>
        </p>
      )}

      {publicUrl && (
        <div className="success-callout flex items-center justify-between gap-3 flex-wrap">
          <div className="min-w-0 flex-1 flex items-center gap-3">
            <Icon.Globe className="shrink-0" size={20} />
            <div className="min-w-0">
              <p className="text-sm font-medium">Cette enquête est en ligne</p>
              <a
                href={publicUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="underline text-xs break-all"
              >
                {publicUrl}
              </a>
            </div>
          </div>
          <div className="flex gap-2 shrink-0">
            <button type="button" className="btn-secondary btn-sm" onClick={() => setQrOpen(true)}>
              <Icon.ExternalLink /> QR code
            </button>
            <button
              type="button"
              className="btn-secondary btn-sm"
              onClick={() => void navigator.clipboard.writeText(publicUrl)}
            >
              <Icon.Copy /> Copier
            </button>
            <a
              href={publicUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-secondary btn-sm"
            >
              <Icon.ExternalLink /> Aperçu
            </a>
          </div>
        </div>
      )}

      <QrCodeDialog
        open={qrOpen}
        url={publicUrl ?? ''}
        title={`QR — ${survey.title}`}
        onClose={() => setQrOpen(false)}
      />

      <DpiaDialog
        open={dpiaOpen}
        workspaceId={workspaceId}
        surveyId={surveyId}
        existing={dpiaQuery.data ?? null}
        onClose={() => {
          setDpiaOpen(false);
          setDiffuseAfterSign(false);
        }}
        onSigned={() => {
          setDpiaOpen(false);
          void queryClient.invalidateQueries({ queryKey: ['dpia', workspaceId, surveyId] });
          // Si la signature faisait suite à un blocage de diffusion, on relance
          // l'ouverture automatiquement (le gating est désormais levé).
          if (diffuseAfterSign) {
            setDiffuseAfterSign(false);
            diffusionMutation.mutate('open');
          }
        }}
      />

      {publishMutation.isError && (
        <p role="alert" className="field-error">
          <Icon.AlertTriangle />
          <span>{getErrorMessage(publishMutation.error)}</span>
        </p>
      )}
      {publishMutation.isSuccess && (
        <div className="success-callout flex items-center gap-2 text-sm" role="status">
          <Icon.Check size={18} /> Publié — v{publishMutation.data?.version}
        </div>
      )}
      {warn.length > 0 && (
        <div role="alert" className="field-error">
          <Icon.AlertTriangle />
          <ul className="list-disc pl-5">
            {warn.map((w, i) => (
              <li key={i}>{w}</li>
            ))}
          </ul>
        </div>
      )}

      <section className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">Blocs ({def.blocks.length})</h2>
          <button type="button" className="btn-secondary btn-sm" onClick={addBlock}>
            + Ajouter un bloc
          </button>
        </div>

        {def.blocks.length === 0 && (
          <div className="empty-state">
            <Icon.ChartBar size={32} className="mx-auto text-text-subtle" />
            <h3 className="mt-3 text-base">Pas encore de bloc</h3>
            <p className="text-sm text-text-muted mt-1">
              Cliquez sur « + Ajouter un bloc » pour commencer à construire votre questionnaire.
            </p>
          </div>
        )}

        {def.blocks.map((block, bIdx) => (
          <BlockEditor
            key={block.id}
            block={block}
            index={bIdx}
            isLast={bIdx === def.blocks.length - 1}
            onChange={(patch) => updateBlock(bIdx, patch)}
            onMoveUp={() => moveBlock(bIdx, -1)}
            onMoveDown={() => moveBlock(bIdx, 1)}
            onRemove={() => removeBlock(bIdx)}
          />
        ))}
      </section>
    </div>
  );
}

// ============================================================================
// Block editor (collapsible) + items
// ============================================================================

interface BlockEditorProps {
  block: Block;
  index: number;
  isLast: boolean;
  onChange: (patch: Partial<Block>) => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onRemove: () => void;
}

function BlockEditor({
  block,
  index,
  isLast,
  onChange,
  onMoveUp,
  onMoveDown,
  onRemove,
}: BlockEditorProps): JSX.Element {
  const [open, setOpen] = useState(true);

  const addItem = (): void => {
    const next: Item = {
      type: 'select_one',
      name: `q_${block.id}_${(block.items?.length ?? 0) + 1}`,
      label: 'Nouvelle question',
      choices: [{ value: 'oui', label: 'Oui' }, { value: 'non', label: 'Non' }],
    };
    onChange({ items: [...(block.items ?? []), next] });
  };

  const updateItem = (i: number, patch: Partial<Item>): void => {
    const items = [...(block.items ?? [])];
    items[i] = { ...items[i]!, ...patch } as Item;
    onChange({ items });
  };
  const moveItem = (i: number, delta: -1 | 1): void => {
    const items = [...(block.items ?? [])];
    const target = i + delta;
    if (target < 0 || target >= items.length) return;
    [items[i], items[target]] = [items[target]!, items[i]!];
    onChange({ items });
  };
  const removeItem = (i: number): void => {
    if (!confirm('Supprimer cette question ?')) return;
    onChange({ items: (block.items ?? []).filter((_, idx) => idx !== i) });
  };

  // ---- Sections ----
  const addSection = (): void => {
    const n = (block.sections?.length ?? 0) + 1;
    const next: Section = { id: `${block.id}_s${n}`, title: `Section ${n}`, items: [] };
    onChange({ sections: [...(block.sections ?? []), next] });
  };
  const updateSection = (i: number, patch: Partial<Section>): void => {
    const sections = [...(block.sections ?? [])];
    sections[i] = { ...sections[i]!, ...patch };
    onChange({ sections });
  };
  const moveSection = (i: number, delta: -1 | 1): void => {
    const sections = [...(block.sections ?? [])];
    const target = i + delta;
    if (target < 0 || target >= sections.length) return;
    [sections[i], sections[target]] = [sections[target]!, sections[i]!];
    onChange({ sections });
  };
  const removeSection = (i: number): void => {
    if (!confirm('Supprimer cette section et toutes ses questions ?')) return;
    onChange({ sections: (block.sections ?? []).filter((_, idx) => idx !== i) });
  };

  const sectionItemsCount = (block.sections ?? []).reduce((s, sec) => s + (sec.items?.length ?? 0), 0);
  const itemsCount = (block.items?.length ?? 0) + sectionItemsCount;

  // Variables disponibles pour les pickers de condition — tous les items du
  // bloc + items de toutes les sections. Pour aller plus loin (items des blocs
  // précédents), il faudrait remonter au niveau de la définition. Pour
  // l'instant on couvre le 80%-cas : conditionner par une question du même bloc.
  const blockAvailableVars = useMemo(() => {
    const out: Array<{ name: string; label: string; type: Item['type']; sensitive?: boolean }> = [];
    for (const it of block.items ?? []) {
      if (it.type === 'note') continue;
      out.push({ name: it.name, label: it.label, type: it.type, sensitive: !!it.meta?.sensitive });
    }
    for (const s of block.sections ?? []) {
      for (const it of s.items ?? []) {
        if (it.type === 'note') continue;
        out.push({ name: it.name, label: it.label, type: it.type, sensitive: !!it.meta?.sensitive });
      }
    }
    return out;
  }, [block.items, block.sections]);

  // Helpers de Context — ajout / suppression de section (utilisés par
  // BranchEditor pour matérialiser une branche conditionnelle).
  const addSectionFromBranch = (section: Section): void => {
    onChange({ sections: [...(block.sections ?? []), section] });
  };
  const removeSectionById = (sectionId: string): void => {
    onChange({ sections: (block.sections ?? []).filter((s) => s.id !== sectionId) });
  };

  return (
    <article className="card card-flush">
      <header className="card-header">
        <button
          type="button"
          className="flex items-center gap-3 flex-1 min-w-0 text-left rounded-md hover:bg-surface-2 -mx-2 px-2 py-1 transition-colors"
          style={{ minHeight: 36 }}
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
        >
          <span className="w-7 h-7 rounded-md bg-primary-soft text-primary-strong inline-flex items-center justify-center text-xs font-semibold shrink-0 tabular-nums">
            {index + 1}
          </span>
          <div className="min-w-0 flex-1">
            <h3 className="text-base font-medium truncate">{block.title || block.id}</h3>
            <p className="text-xs text-text-muted">
              {itemsCount} question{itemsCount === 1 ? '' : 's'}
              {block.relevant && (
                <span className="ml-2 inline-flex items-center gap-1 text-text-subtle">
                  <Icon.Info size={11} /> conditionnel
                </span>
              )}
            </p>
          </div>
          <span className="shrink-0 text-text-subtle">
            {open ? <Icon.ChevronDown /> : <Icon.ChevronRight />}
          </span>
        </button>
        <div className="flex gap-0.5 shrink-0">
          <IconButton label="Monter" onClick={onMoveUp} disabled={index === 0}>
            <Icon.ArrowUp />
          </IconButton>
          <IconButton label="Descendre" onClick={onMoveDown} disabled={isLast}>
            <Icon.ArrowDown />
          </IconButton>
          <IconButton label="Supprimer" tone="danger" onClick={onRemove}>
            <Icon.Trash />
          </IconButton>
        </div>
      </header>

      {open && (
        <BranchContextProvider
          value={{
            sectionsOfBlock: block.sections ?? [],
            addSection: addSectionFromBranch,
            removeSection: removeSectionById,
            availableVars: blockAvailableVars,
          }}
        >
        <div className="card-section space-y-4">
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="field-label">Identifiant (id)</label>
              <input
                type="text"
                className="input-base font-mono text-sm"
                value={block.id}
                onChange={(e) => onChange({ id: e.target.value })}
              />
            </div>
            <div>
              <label className="field-label">Titre affiché</label>
              <input
                type="text"
                className="input-base"
                value={block.title ?? ''}
                onChange={(e) => onChange({ title: e.target.value })}
              />
            </div>
          </div>
          <div>
            <label className="field-label">Description (optionnelle)</label>
            <textarea
              className="input-base"
              rows={2}
              value={block.description ?? ''}
              onChange={(e) => onChange({ description: e.target.value })}
            />
          </div>
          <div>
            <label className="field-label">
              Afficher ce bloc uniquement si <span className="text-text-subtle font-normal">(optionnel)</span>
            </label>
            <ExpertRelevantInput
              value={block.relevant ?? ''}
              onChange={(next) => onChange({ relevant: next })}
              availableVars={blockAvailableVars}
              placeholder='${consentement} == "accepte"'
              ariaLabel="Expression relevant du bloc"
            />
          </div>

          <div className="space-y-3 pt-2 border-t border-surface-2">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-medium">
                Questions directes
                <span className="text-text-subtle font-normal"> (hors section)</span>
              </h4>
              <button type="button" className="btn-secondary text-sm" onClick={addItem}>
                + Ajouter une question
              </button>
            </div>
            {(block.items ?? []).length === 0 && (
              <p className="text-xs text-text-muted">
                Aucune question directe. Vous pouvez en ajouter, ou regrouper les questions
                dans des sections ci-dessous.
              </p>
            )}
            {(block.items ?? []).map((item, iIdx) => (
              <ItemEditor
                key={`${block.id}-${iIdx}`}
                item={item}
                index={iIdx}
                isLast={iIdx === (block.items?.length ?? 0) - 1}
                onChange={(patch) => updateItem(iIdx, patch)}
                onMoveUp={() => moveItem(iIdx, -1)}
                onMoveDown={() => moveItem(iIdx, 1)}
                onRemove={() => removeItem(iIdx)}
              />
            ))}
          </div>

          {/* Sections : regroupements de questions à l'intérieur du bloc. */}
          <div className="space-y-3 pt-3 border-t border-surface-2">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-medium">
                Sections ({block.sections?.length ?? 0})
              </h4>
              <button type="button" className="btn-secondary text-sm" onClick={addSection}>
                + Ajouter une section
              </button>
            </div>
            {(block.sections ?? []).map((section, sIdx) => (
              <SectionEditor
                key={`${block.id}-sec-${sIdx}`}
                section={section}
                index={sIdx}
                isLast={sIdx === (block.sections?.length ?? 0) - 1}
                onChange={(patch) => updateSection(sIdx, patch)}
                onMoveUp={() => moveSection(sIdx, -1)}
                onMoveDown={() => moveSection(sIdx, 1)}
                onRemove={() => removeSection(sIdx)}
              />
            ))}
          </div>
        </div>
        </BranchContextProvider>
      )}
    </article>
  );
}

// ============================================================================
// Section editor (regroupement de questions dans un bloc)
// ============================================================================

interface SectionEditorProps {
  section: Section;
  index: number;
  isLast: boolean;
  onChange: (patch: Partial<Section>) => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onRemove: () => void;
}

function SectionEditor({
  section,
  index,
  isLast,
  onChange,
  onMoveUp,
  onMoveDown,
  onRemove,
}: SectionEditorProps): JSX.Element {
  const [open, setOpen] = useState(true);
  const items = section.items ?? [];

  const addItem = (): void => {
    const next: Item = {
      type: 'select_one',
      name: `q_${section.id}_${items.length + 1}`,
      label: 'Nouvelle question',
      choices: [{ value: 'oui', label: 'Oui' }, { value: 'non', label: 'Non' }],
    };
    onChange({ items: [...items, next] });
  };
  const updateItem = (i: number, patch: Partial<Item>): void => {
    const next = [...items];
    next[i] = { ...next[i]!, ...patch } as Item;
    onChange({ items: next });
  };
  const moveItem = (i: number, delta: -1 | 1): void => {
    const next = [...items];
    const target = i + delta;
    if (target < 0 || target >= next.length) return;
    [next[i], next[target]] = [next[target]!, next[i]!];
    onChange({ items: next });
  };
  const removeItem = (i: number): void => {
    if (!confirm('Supprimer cette question ?')) return;
    onChange({ items: items.filter((_, idx) => idx !== i) });
  };

  return (
    <div className="rounded-lg border border-border bg-bg-deep/30">
      <header className="flex items-center justify-between gap-2 px-3 py-2 border-b border-border">
        <button
          type="button"
          className="flex items-center gap-2 flex-1 min-w-0 text-left rounded-md hover:bg-surface-2 px-1 transition-colors"
          style={{ minHeight: 32 }}
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
        >
          <span className="text-text-subtle shrink-0">
            {open ? <Icon.ChevronDown size={14} /> : <Icon.ChevronRight size={14} />}
          </span>
          <span className="text-xs uppercase tracking-wide text-text-muted shrink-0">
            Section {index + 1}
          </span>
          <span className="text-sm font-medium truncate">{section.title || section.id}</span>
          <span className="text-xs text-text-subtle shrink-0">
            · {items.length} question{items.length === 1 ? '' : 's'}
          </span>
        </button>
        <div className="flex gap-0.5 shrink-0">
          <IconButton label="Monter la section" onClick={onMoveUp} disabled={index === 0}>
            <Icon.ArrowUp />
          </IconButton>
          <IconButton label="Descendre la section" onClick={onMoveDown} disabled={isLast}>
            <Icon.ArrowDown />
          </IconButton>
          <IconButton label="Supprimer la section" tone="danger" onClick={onRemove}>
            <Icon.Trash />
          </IconButton>
        </div>
      </header>

      {open && (
        <div className="p-3 space-y-3">
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="field-label">Identifiant (id)</label>
              <input
                type="text"
                className="input-base font-mono text-sm"
                value={section.id}
                onChange={(e) => onChange({ id: e.target.value })}
              />
            </div>
            <div>
              <label className="field-label">Titre affiché</label>
              <input
                type="text"
                className="input-base"
                value={section.title ?? ''}
                onChange={(e) => onChange({ title: e.target.value })}
              />
            </div>
          </div>
          <div>
            <label className="field-label">
              Introduction de la section
              <span className="text-text-subtle font-normal"> (optionnelle, affichée par le wizard répondant)</span>
            </label>
            <textarea
              className="input-base"
              rows={3}
              maxLength={300}
              value={section.intro ?? ''}
              onChange={(e) => onChange({ intro: e.target.value })}
              placeholder="3 à 5 lignes : finalité de la section, vocabulaire si besoin. Affichée au respondent avant la 1ère question (wizard v1.2). Si vide ET aucun item sensible, l'écran d'intro est sauté."
            />
            <p className="text-xs text-text-muted mt-1">
              {(section.intro ?? '').length} / 300 caractères
            </p>
          </div>
          <SectionRelevantField
            value={section.relevant ?? ''}
            onChange={(next) => onChange({ relevant: next })}
            isBranch={section.id.startsWith('branch__')}
          />

          <div className="space-y-3 pt-2 border-t border-border">
            <div className="flex items-center justify-between">
              <h5 className="text-sm font-medium">Questions de la section</h5>
              <button type="button" className="btn-secondary text-sm" onClick={addItem}>
                + Ajouter une question
              </button>
            </div>
            {items.map((item, iIdx) => (
              <ItemEditor
                key={`${section.id}-${iIdx}`}
                item={item}
                index={iIdx}
                isLast={iIdx === items.length - 1}
                onChange={(patch) => updateItem(iIdx, patch)}
                onMoveUp={() => moveItem(iIdx, -1)}
                onMoveDown={() => moveItem(iIdx, 1)}
                onRemove={() => removeItem(iIdx)}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// SectionRelevantField — A (picker simple) + C (mode avancé XLSForm)
// ============================================================================

function SectionRelevantField({
  value,
  onChange,
  isBranch,
}: {
  value: string;
  onChange: (next: string) => void;
  isBranch: boolean;
}): JSX.Element {
  const ctx = useBranchContext();
  const [showExpert, setShowExpert] = useState(false);

  // Si c'est une branche auto-générée par BranchEditor, on affiche un badge
  // verrouillé pour éviter que l'utilisateur ne casse la liaison choix↔branche
  // en éditant le `relevant` à la main. Pour passer outre : « Mode avancé ».
  if (isBranch && !showExpert) {
    return (
      <div className="rounded-md border border-border bg-bg-deep/30 p-3 text-xs space-y-2">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <span className="text-text-muted">
            <Icon.Info size={11} className="inline align-baseline mr-1" />
            Branche conditionnelle automatique — visible si le choix associé est coché.
          </span>
          <button
            type="button"
            className="btn-ghost text-[11px]"
            onClick={() => setShowExpert(true)}
          >
            Mode avancé
          </button>
        </div>
        <code className="block font-mono text-[11px] text-text-subtle bg-surface px-2 py-1 rounded">
          relevant : {value || '—'}
        </code>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <label className="field-label mb-0">
          Afficher cette section uniquement si{' '}
          <span className="text-text-subtle font-normal">(optionnel)</span>
        </label>
        <button
          type="button"
          className="btn-ghost text-xs"
          onClick={() => setShowExpert((v) => !v)}
        >
          {showExpert ? 'Picker visuel' : 'Mode avancé (XLSForm)'}
        </button>
      </div>
      {showExpert ? (
        <ExpertRelevantInput
          value={value}
          onChange={onChange}
          availableVars={ctx?.availableVars ?? []}
          placeholder='${b1q1} == "oui"'
          ariaLabel="Expression relevant de la section"
        />
      ) : (
        <ConditionPicker
          availableVars={ctx?.availableVars ?? []}
          value={value}
          onChange={onChange}
        />
      )}
    </div>
  );
}

// ============================================================================
// Item editor (per-type fields)
// ============================================================================

interface ItemEditorProps {
  item: Item;
  index: number;
  isLast: boolean;
  onChange: (patch: Partial<Item>) => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onRemove: () => void;
}

const ITEM_TYPES: ItemType[] = [
  'note',
  'select_one',
  'select_multiple',
  'integer',
  'text_short',
  'text_long',
  'matrix',
];

function ItemEditor({
  item,
  index,
  isLast,
  onChange,
  onMoveUp,
  onMoveDown,
  onRemove,
}: ItemEditorProps): JSX.Element {
  const setMeta = (k: 'sensitive' | 'pii_risk', v: boolean): void => {
    onChange({ meta: { ...(item.meta ?? {}), [k]: v } });
  };

  return (
    <div className="bg-bg/50 rounded-lg p-4 border border-border space-y-3">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-text-muted">Q{index + 1}</span>
          <Badge tone="neutral">{labelType(item.type)}</Badge>
          {item.meta?.sensitive && (
            <Badge tone="danger">Sensible (art. 9/10)</Badge>
          )}
          {item.meta?.pii_risk && (
            <Badge tone="notice">PII libre</Badge>
          )}
        </div>
        <div className="flex gap-0.5">
          <IconButton label="Monter" onClick={onMoveUp} disabled={index === 0}>
            <Icon.ArrowUp />
          </IconButton>
          <IconButton label="Descendre" onClick={onMoveDown} disabled={isLast}>
            <Icon.ArrowDown />
          </IconButton>
          <IconButton label="Supprimer" tone="danger" onClick={onRemove}>
            <Icon.Trash />
          </IconButton>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <label className="field-label">Type</label>
          <select
            className="input-base"
            value={item.type}
            onChange={(e) => {
              const t = e.target.value as ItemType;
              // Reset des champs spécifiques au changement de type.
              const base = { name: item.name, label: item.label, type: t };
              const next =
                t === 'select_one' || t === 'select_multiple'
                  ? { ...base, choices: [{ value: 'oui', label: 'Oui' }] }
                  : t === 'matrix'
                  ? {
                      ...base,
                      rows: [{ name: 'row1', label: 'Ligne 1' }],
                      choices: [{ value: '1', label: '1' }],
                    }
                  : base;
              onChange(next as Partial<Item>);
            }}
          >
            {ITEM_TYPES.map((t) => (
              <option key={t} value={t}>
                {labelType(t)}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="field-label">name (variable)</label>
          <input
            type="text"
            className="input-base font-mono text-sm"
            value={item.name}
            onChange={(e) => onChange({ name: e.target.value })}
            placeholder="b1q1"
            pattern="^[a-z][a-z0-9_]*$"
          />
        </div>
      </div>

      <div>
        <label className="field-label">Libellé (affiché au répondant)</label>
        <textarea
          className="input-base"
          rows={2}
          value={item.label}
          onChange={(e) => onChange({ label: e.target.value })}
        />
      </div>

      {item.type !== 'note' && (
        <div className="grid sm:grid-cols-2 gap-3">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={!!item.required}
              onChange={(e) => onChange({ required: e.target.checked })}
            />
            Obligatoire
          </label>
          <label className="flex items-center gap-2 text-sm" title="Donnée art. 9/10 — chiffrée">
            <input
              type="checkbox"
              checked={!!item.meta?.sensitive}
              onChange={(e) => setMeta('sensitive', e.target.checked)}
            />
            meta.sensitive (art. 9/10)
          </label>
          <label className="flex items-center gap-2 text-sm" title="Risque PII auto-déclarée">
            <input
              type="checkbox"
              checked={!!item.meta?.pii_risk}
              onChange={(e) => setMeta('pii_risk', e.target.checked)}
            />
            meta.pii_risk (champ libre)
          </label>
        </div>
      )}

      {/* Per-type panels */}
      {(item.type === 'select_one' || item.type === 'select_multiple') && (
        <ChoicesEditor
          choices={item.choices ?? []}
          onChange={(choices) => onChange({ choices } as Partial<Item>)}
        />
      )}

      {item.type === 'select_multiple' && (
        <div>
          <label className="field-label">Options exclusives (CSV)</label>
          <input
            type="text"
            className="input-base font-mono text-sm"
            value={(item.exclusive ?? []).join(',')}
            onChange={(e) =>
              onChange({
                exclusive: e.target.value
                  .split(',')
                  .map((s) => s.trim())
                  .filter(Boolean),
              } as Partial<Item>)
            }
            placeholder="non_concerne, aucun"
          />
          <p className="text-xs text-text-muted mt-1">
            Si cochée, désélectionne les autres (ex. « Non concerné »).
          </p>
        </div>
      )}

      {item.type === 'integer' && (
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="field-label">min</label>
            <input
              type="number"
              className="input-base"
              value={item.min ?? ''}
              onChange={(e) =>
                onChange({ min: e.target.value === '' ? undefined : Number(e.target.value) } as Partial<Item>)
              }
            />
          </div>
          <div>
            <label className="field-label">max</label>
            <input
              type="number"
              className="input-base"
              value={item.max ?? ''}
              onChange={(e) =>
                onChange({ max: e.target.value === '' ? undefined : Number(e.target.value) } as Partial<Item>)
              }
            />
          </div>
        </div>
      )}

      {(item.type === 'text_short' || item.type === 'text_long') && (
        <div>
          <label className="field-label">maxLength</label>
          <input
            type="number"
            className="input-base w-32"
            value={item.maxLength ?? ''}
            onChange={(e) =>
              onChange({ maxLength: e.target.value === '' ? undefined : Number(e.target.value) } as Partial<Item>)
            }
          />
        </div>
      )}

      {item.type === 'matrix' && (
        <MatrixEditor
          rows={item.rows ?? []}
          choices={item.choices ?? []}
          onRowsChange={(rows) => onChange({ rows } as Partial<Item>)}
          onChoicesChange={(choices) => onChange({ choices } as Partial<Item>)}
        />
      )}

      {item.type !== 'note' && (
        <div className="space-y-3">
          <div>
            <label className="field-label">
              Afficher uniquement si <span className="text-text-subtle font-normal">(optionnel)</span>
            </label>
            <ItemRelevantField item={item} onChange={onChange} />
          </div>
          <div>
            <label className="field-label">
              Contrainte de validité <span className="text-text-subtle font-normal">(constraint)</span>
            </label>
            <input
              type="text"
              className="input-base font-mono text-sm"
              value={item.constraint ?? ''}
              onChange={(e) => onChange({ constraint: e.target.value } as Partial<Item>)}
              placeholder='${b1q2} >= 18'
            />
          </div>
        </div>
      )}

      {/* Branches conditionnelles — uniquement pour les questions à choix.
          La branche est matérialisée par une section dans le bloc parent
          (id `branch__<item>__<choice>`). Le BranchEditor lit / crée / supprime
          ces sections via le BranchContext. */}
      {(item.type === 'select_one' || item.type === 'select_multiple') && (
        <ItemBranchSlot item={item} />
      )}
    </div>
  );
}

/** Sous-composant : input `relevant` d'item, avec WARDEN lint. */
function ItemRelevantField({
  item,
  onChange,
}: {
  item: Item;
  onChange: (patch: Partial<Item>) => void;
}): JSX.Element {
  const ctx = useBranchContext();
  return (
    <>
      <ExpertRelevantInput
        value={item.relevant ?? ''}
        onChange={(next) => onChange({ relevant: next } as Partial<Item>)}
        availableVars={ctx?.availableVars ?? []}
        placeholder='${b1q1} == "oui"'
        ariaLabel="Expression relevant de la question"
      />
      <WardenLintBadge
        relevant={item.relevant ?? ''}
        availableVars={ctx?.availableVars ?? []}
        ownerSensitive={!!item.meta?.sensitive}
      />
    </>
  );
}

/** Sous-composant : BranchEditor pour items à choix, lié au contexte. */
function ItemBranchSlot({
  item,
}: {
  item: Extract<Item, { type: 'select_one' | 'select_multiple' }>;
}): JSX.Element | null {
  const ctx = useBranchContext();
  if (!ctx) return null;
  return (
    <BranchEditor
      item={item}
      sectionsOfBlock={ctx.sectionsOfBlock}
      onAddSection={ctx.addSection}
      onRemoveSection={ctx.removeSection}
    />
  );
}

// ============================================================================
// Sub-editors
// ============================================================================

function ChoicesEditor({
  choices,
  onChange,
}: {
  choices: Choice[];
  onChange: (next: Choice[]) => void;
}): JSX.Element {
  return (
    <div>
      <label className="field-label">Choix proposés</label>
      <ul className="space-y-1">
        {choices.map((c, i) => (
          <li key={i} className="flex items-center gap-2">
            <input
              type="text"
              className="input-base font-mono text-sm flex-1"
              value={c.value}
              onChange={(e) => {
                const next = [...choices];
                next[i] = { ...c, value: e.target.value };
                onChange(next);
              }}
              placeholder="value"
            />
            <input
              type="text"
              className="input-base flex-1"
              value={c.label}
              onChange={(e) => {
                const next = [...choices];
                next[i] = { ...c, label: e.target.value };
                onChange(next);
              }}
              placeholder="Libellé affiché"
            />
            <button
              type="button"
              className="btn-ghost text-sm text-danger"
              onClick={() => onChange(choices.filter((_, idx) => idx !== i))}
              aria-label="Supprimer ce choix"
            >
              ✕
            </button>
          </li>
        ))}
      </ul>
      <button
        type="button"
        className="btn-ghost text-sm mt-2"
        onClick={() =>
          onChange([...choices, { value: `c${choices.length + 1}`, label: '' }])
        }
      >
        + Ajouter un choix
      </button>
    </div>
  );
}

function MatrixEditor({
  rows,
  choices,
  onRowsChange,
  onChoicesChange,
}: {
  rows: { name: string; label: string }[];
  choices: Choice[];
  onRowsChange: (rows: { name: string; label: string }[]) => void;
  onChoicesChange: (c: Choice[]) => void;
}): JSX.Element {
  return (
    <div className="grid sm:grid-cols-2 gap-4">
      <div>
        <label className="field-label">Lignes (items mesurés)</label>
        {rows.map((r, i) => (
          <div key={i} className="flex items-center gap-2 mb-1">
            <input
              type="text"
              className="input-base font-mono text-sm w-24"
              value={r.name}
              onChange={(e) => {
                const next = [...rows];
                next[i] = { ...r, name: e.target.value };
                onRowsChange(next);
              }}
              placeholder="row1"
            />
            <input
              type="text"
              className="input-base flex-1"
              value={r.label}
              onChange={(e) => {
                const next = [...rows];
                next[i] = { ...r, label: e.target.value };
                onRowsChange(next);
              }}
              placeholder="Libellé"
            />
            <button
              type="button"
              className="btn-ghost text-sm text-danger"
              onClick={() => onRowsChange(rows.filter((_, idx) => idx !== i))}
              aria-label="Supprimer cette ligne"
            >
              ✕
            </button>
          </div>
        ))}
        <button
          type="button"
          className="btn-ghost text-sm"
          onClick={() =>
            onRowsChange([...rows, { name: `row${rows.length + 1}`, label: '' }])
          }
        >
          + Ligne
        </button>
      </div>
      <div>
        <label className="field-label">Échelle (colonnes)</label>
        <ChoicesEditor choices={choices} onChange={onChoicesChange} />
      </div>
    </div>
  );
}

// ============================================================================
// Misc
// ============================================================================

function statusLabel(status: 'draft' | 'published' | 'closed' | 'archived'): string {
  if (status === 'draft') return 'Brouillon';
  if (status === 'published') return 'Publiée';
  if (status === 'archived') return 'Archivée';
  return 'Fermée';
}
function statusTone(
  status: 'draft' | 'published' | 'closed' | 'archived',
): 'neutral' | 'success' | 'notice' | 'lock' {
  if (status === 'draft') return 'neutral';
  if (status === 'published') return 'success';
  if (status === 'archived') return 'lock';
  return 'notice';
}
function identityLabel(m: 'anonymous' | 'optional' | 'required'): string {
  if (m === 'anonymous') return 'Anonyme';
  if (m === 'optional') return 'Opt-in';
  return 'Identifiée';
}
function labelType(t: ItemType): string {
  return (
    {
      note: 'Note (affichage)',
      select_one: 'Choix unique',
      select_multiple: 'Choix multiple',
      integer: 'Nombre entier',
      text_short: 'Texte court',
      text_long: 'Texte long',
      matrix: 'Matrice Likert',
    }[t] ?? t
  );
}
